import { useEffect, useState } from 'react';
import { checkReady, HealthCheckError, type ReadyResponse } from '../api/health';

// HealthState is a discriminated union so the renderer can switch on
// the discriminant without parsing strings.
export type HealthState =
  | { kind: 'checking' }
  | { kind: 'healthy'; response: ReadyResponse }
  | { kind: 'degraded'; code: string; message: string }
  | { kind: 'error'; message: string };

export interface HealthStatusProps {
  // Optional: override the polling interval. Default 30s.
  // Tests override this to 0 (poll once on mount).
  pollIntervalMs?: number;
}

// HealthStatus polls /api/v1/health/ready on mount and renders the
// current state with role=status so assistive tech can announce it.
//
// In P1 this component is the only dynamic UI; business features (room
// creation, etc.) are deferred to later phases per docs/product-requirements.md.
export function HealthStatus({ pollIntervalMs = 30_000 }: HealthStatusProps) {
  const [state, setState] = useState<HealthState>({ kind: 'checking' });

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const run = async () => {
      try {
        const resp = await checkReady(controller.signal);
        if (!cancelled) setState({ kind: 'healthy', response: resp });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof HealthCheckError) {
          setState({ kind: 'degraded', code: err.code, message: err.message });
        } else if (err instanceof DOMException && err.name === 'AbortError') {
          return;
        } else {
          setState({ kind: 'error', message: err instanceof Error ? err.message : 'unknown error' });
        }
      }
    };

    void run();

    if (pollIntervalMs > 0) {
      const id = window.setInterval(run, pollIntervalMs);
      return () => {
        cancelled = true;
        controller.abort();
        window.clearInterval(id);
      };
    }
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [pollIntervalMs]);

  const label = stateToLabel(state);

  return (
    <div role="status" aria-live="polite" data-state={state.kind}>
      {label}
    </div>
  );
}

function stateToLabel(state: HealthState): string {
  switch (state.kind) {
    case 'checking':
      return '检查中…';
    case 'healthy':
      return '服务正常';
    case 'degraded':
      return `服务降级：${state.message}`;
    case 'error':
      return `服务异常：${state.message}`;
  }
}
